import { useContext, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Box, debounce } from '@material-ui/core';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { useGetIemsQuery } from './utils/callApis';
import allActions from './actions/product_builder';
// Summary page components
import FetchInitialData from './utils/InitialDataFetch';
import MobileLayout from './container/mobile';
import DesktopLayout from './container/desktop';
import './styles/style.css';
import useCustomLayers from './hooks/useCustomLayers';
import useAddToCart from './hooks/useAddToCart';
import { validateUserInfo } from './utils/validateUserInfo';
import { PreviewDataContext } from './context/PreviewContext';

let theme = createTheme({
    palette: {
        text: {
            primary: '#707070',
            secondary: '#727272',
            black: '#000000'
        }
    },
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
            '"Montserrat"'
        ].join(',')
    }
});

const App = ({ model, configid }) => {
    const dispatch = useDispatch();
    const previewBlob = useSelector((state) => state.rootReducer.preview.url);
    const { setPreview } = useContext(PreviewDataContext);
    // const layers = useSelector((state) => state.rootReducer.customLayer);
    const { data: iems, isLoading: isIemsLoading, isError, error } = useGetIemsQuery();
    let summaryState = allActions.categoryActions;
    const formInfo = useSelector((state) => state.rootReducer.finalizeForm);
    const modelList = useSelector((state) => state.rootReducer.modelList.modelList);
    const { isLoading: isFetchingInitialData } = FetchInitialData({
        iems,
        model,
        configid
    });

    const isLoading = useMemo(
        () => isIemsLoading || isFetchingInitialData,
        [isIemsLoading, isFetchingInitialData]
    );

    useEffect(() => {
        if (validateUserInfo(formInfo)) {
            dispatch(summaryState.updateSummaryCategory(true));
        }
    }, [formInfo]);

    /* Note: should we inject here or not? */
    const [layerChanges, setLayerChanges] = useState(); // for tracking changes but should probably move to redux to be able to "reset" selections.
    const [width, setWidth] = useState(window.innerWidth);
    const { addToCart } = useAddToCart();
    const currentSide = useSelector((state) => state.rootReducer.settingsOptions.currentSide);
    const { onLoad, onFlip, onMirror, onReset, onCenter, handleLayerChange, layers } =
        useCustomLayers(setPreview, layerChanges, setLayerChanges, width);

    useEffect(() => {
        const debouncedResize = debounce(() => setWidth(window.innerWidth), 250);
        window.addEventListener('resize', debouncedResize);
        // getImage(blob);
        setPreview(previewBlob);
        return () => window.removeEventListener('resize', debouncedResize);
    }, []);
    useEffect(() => {
        batch(() => {
            dispatch(allActions.settingsActions.getSideOptions());
            if (currentSide === undefined) {
                dispatch(allActions.settingsActions.setCurrentSide('both'));
            }
        });
    }, [currentSide]);

    if (isLoading) {
        return (
            <Box>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return <div>{error.toString()}</div>;
    }

    return (
        <ThemeProvider theme={theme}>
            {width < 800 ? (
                <MobileLayout
                    width={width}
                    sortedList={modelList}
                    layerChanges={layerChanges}
                    setLayerChanges={setLayerChanges}
                    layers={layers}
                    handleLayerChange={handleLayerChange}
                    onLoad={onLoad}
                    onFlip={onFlip}
                    onMirror={onMirror}
                    onReset={onReset}
                    onCenter={onCenter}
                    addToCart={addToCart}
                />
            ) : (
                <DesktopLayout
                    width={width}
                    sortedList={modelList}
                    layerChanges={layerChanges}
                    setLayerChanges={setLayerChanges}
                    layers={layers}
                    onLoad={onLoad}
                    handleLayerChange={handleLayerChange}
                    onFlip={onFlip}
                    onMirror={onMirror}
                    onReset={onReset}
                    onCenter={onCenter}
                    addToCart={addToCart}
                />
            )}
        </ThemeProvider>
    );
};

export default App;

export function sortModelsByPrice(modelList, shopiftyList) {
    const shopifyListProducts = shopiftyList.map((item) => {
        return item.title;
    });

    const list = modelList.slice().sort((a, b) => {
        if (parseInt(a.price) > parseInt(b.price)) {
            return 1;
        } else if (parseInt(a.price) < parseInt(b.price)) {
            return -1;
        }
        return 0;
    });

    const listOfActive = list.filter((item) => {
        if (shopifyListProducts.includes(item.name)) {
            return item;
        }
    });

    return listOfActive;
}
